import { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  max-width: 90vw;
  height: 100vh;
  background: var(--bg-1, white);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow-y: auto;
  padding: 24px;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
`;

const Header = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--bg-3);
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-1);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--text-2);
`;

const UserCard = styled.div`
  padding: 12px;
  background: var(--bg-2);
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid var(--bg-3);
`;

const UserEmail = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--text-1);
  margin-bottom: 4px;
`;

const UserMeta = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-bottom: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'approve' && `
    background: #48bb78;
    color: white;
    &:hover { background: #38a169; }
  `}

  ${props => props.variant === 'reject' && `
    background: #f56565;
    color: white;
    &:hover { background: #e53e3e; }
  `}
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;

  ${props => props.type === 'admin' && `
    background: #2196f3;
    color: white;
  `}

  ${props => props.type === 'approved' && `
    background: #48bb78;
    color: white;
  `}

  ${props => props.type === 'pending' && `
    background: #ed8936;
    color: white;
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-2);
  font-size: 14px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-2);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: var(--bg-3);
  }
`;

export default function AdminPanel({ isOpen, onClose }) {
  const [pending, setPending] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('pending'); // 'pending' or 'all'

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('bd_auth_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [pendingRes, allRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/auth/admin/pending`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/auth/admin/users`, { headers })
      ]);

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPending(data.pending);
      }

      if (allRes.ok) {
        const data = await allRes.json();
        setAllUsers(data.users);
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const handleApprove = async (email) => {
    const token = localStorage.getItem('bd_auth_token');
    await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/auth/admin/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });
    fetchData();
  };

  const handleReject = async (email) => {
    if (!window.confirm(`Are you sure you want to reject ${email}?`)) return;

    const token = localStorage.getItem('bd_auth_token');
    await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/auth/admin/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });
    fetchData();
  };

  return (
    <>
    <Overlay isOpen={isOpen} onClick={onClose} />
    <Panel isOpen={isOpen}>
      <CloseButton onClick={onClose}>×</CloseButton>

      <Header>
        <Title>User Management</Title>
        <Subtitle>Approve or reject user access requests</Subtitle>
      </Header>

      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <button
          onClick={() => setView('pending')}
          style={{
            flex: 1,
            padding: '8px',
            background: view === 'pending' ? 'var(--purple)' : 'var(--bg-3)',
            color: view === 'pending' ? 'white' : 'var(--text-2)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Pending ({pending.length})
        </button>
        <button
          onClick={() => setView('all')}
          style={{
            flex: 1,
            padding: '8px',
            background: view === 'all' ? 'var(--purple)' : 'var(--bg-3)',
            color: view === 'all' ? 'white' : 'var(--text-2)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          All Users ({allUsers.length})
        </button>
      </div>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : view === 'pending' ? (
        pending.length === 0 ? (
          <EmptyState>No pending approval requests</EmptyState>
        ) : (
          pending.map(user => (
            <UserCard key={user.email}>
              <UserEmail>{user.email}</UserEmail>
              <UserMeta>
                {user.name}<br />
                Requested: {new Date(user.createdAt).toLocaleDateString()}
              </UserMeta>
              <ButtonGroup>
                <ActionButton variant="approve" onClick={() => handleApprove(user.email)}>
                  Approve
                </ActionButton>
                <ActionButton variant="reject" onClick={() => handleReject(user.email)}>
                  Reject
                </ActionButton>
              </ButtonGroup>
            </UserCard>
          ))
        )
      ) : (
        allUsers.length === 0 ? (
          <EmptyState>No users found</EmptyState>
        ) : (
          allUsers.map(user => (
            <UserCard key={user.email}>
              <UserEmail>
                {user.email}
                {' '}
                {user.role === 'admin' && <Badge type="admin">Admin</Badge>}
                {user.role !== 'admin' && user.approved && <Badge type="approved">Active</Badge>}
                {!user.approved && <Badge type="pending">Pending</Badge>}
              </UserEmail>
              <UserMeta>
                {user.name}<br />
                Joined: {new Date(user.createdAt).toLocaleDateString()}
              </UserMeta>
              {user.role !== 'admin' && (
                <ButtonGroup>
                  {!user.approved && (
                    <ActionButton variant="approve" onClick={() => handleApprove(user.email)}>
                      Approve
                    </ActionButton>
                  )}
                  <ActionButton variant="reject" onClick={() => handleReject(user.email)}>
                    Remove
                  </ActionButton>
                </ButtonGroup>
              )}
            </UserCard>
          ))
        )
      )}
    </Panel>
    </>
  );
}
